// apps/web/src/pages/Announcements/components/SanitizedHtml.tsx
//
// Render seguro de rich-text (HTML) de um comunicado. Sanitiza com DOMPurify
// antes de injetar via dangerouslySetInnerHTML — defesa em profundidade
// espelhando 1:1 a allowlist do backend (announcement-html.sanitizer.ts).
//
// Dois profiles:
// - 'body' (padrão): corpo do comunicado — p/br/strong/b/em/i/u/s/ul/ol/li/
//   h1-h4/blockquote/a/span; em <a> só href/title/target/rel com schemes
//   http|https|mailto (rel/target forçados); style só em blocos e filtrado a
//   text-align: left|right|center|justify.
// - 'inline': resumo/teaser do card — SÓ inline + p/br (p/br/strong/b/em/i/u/s/
//   a/span); em <a> só href/title/target/rel; SEM style e SEM blocos (h1-4/ul/
//   ol/li/blockquote somem). Espelha a allowlist inline do backend 1:1.

import { useMemo } from 'react';

import DOMPurify from 'dompurify';

import { cn } from '@alamo/ui';

export type SanitizedHtmlProfile = 'body' | 'inline';

const BODY_ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'blockquote',
  'a',
  'span',
];
const BODY_ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'style'];

// Inline: só marcas inline + p/br; <a> sem style. Sem blocos.
const INLINE_ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'a', 'span'];
const INLINE_ALLOWED_ATTR = ['href', 'title', 'target', 'rel'];

// Só http/https/mailto — bloqueia javascript:/data:/vbscript: e protocol-relative.
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:)/i;

// style permitido só nestes blocos, e só com text-align.
const STYLE_ALLOWED_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE', 'LI']);
const TEXT_ALIGN_RE = /^(left|right|center|justify)$/i;

let hooksRegistered = false;

function registerHooks(): void {
  if (hooksRegistered) return;
  hooksRegistered = true;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!(node instanceof Element)) return;

    if (node.tagName === 'A') {
      // Links: revalida o scheme do href e só força rel/target quando o href
      // sobreviveu à filtragem (alinhado ao backend — sem href não há alvo).
      const href = node.getAttribute('href');
      if (href && !ALLOWED_URI_REGEXP.test(href.trim())) {
        node.removeAttribute('href');
      }
      if (node.hasAttribute('href')) {
        node.setAttribute('rel', 'noopener noreferrer');
        node.setAttribute('target', '_blank');
      }
    } else if (node.hasAttribute('title')) {
      // `title` só é permitido em <a> (paridade com o backend); descarta o resto.
      node.removeAttribute('title');
    }

    // style: manter só `text-align` em blocos permitidos; descartar o resto.
    if (node.hasAttribute('style') && node instanceof HTMLElement) {
      const align = node.style.textAlign;
      const keep = STYLE_ALLOWED_TAGS.has(node.tagName) && TEXT_ALIGN_RE.test(align);
      if (keep) {
        node.setAttribute('style', `text-align:${align.toLowerCase()}`);
      } else {
        node.removeAttribute('style');
      }
    } else if (node.hasAttribute('style')) {
      node.removeAttribute('style');
    }
  });
}

export interface SanitizedHtmlProps {
  html: string;
  /** 'body' (padrão) = corpo completo; 'inline' = resumo/teaser (só marcas inline). */
  profile?: SanitizedHtmlProfile;
  className?: string;
}

export function SanitizedHtml({ html, profile = 'body', className }: SanitizedHtmlProps) {
  const clean = useMemo(() => {
    registerHooks();
    const isInline = profile === 'inline';
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: isInline ? INLINE_ALLOWED_TAGS : BODY_ALLOWED_TAGS,
      ALLOWED_ATTR: isInline ? INLINE_ALLOWED_ATTR : BODY_ALLOWED_ATTR,
      ALLOWED_URI_REGEXP,
    });
  }, [html, profile]);

  return (
    <div
      className={cn(
        'prose prose-sm max-w-none font-body text-slate-700',
        profile === 'body' && 'announcement-rich-text',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
